import React, { useState } from "react";
import { Box, Typography, Button, Tooltip, Chip, IconButton, Modal, Menu, MenuItem, Divider, CircularProgress } from "@mui/material";
import { useTheme, useMediaQuery } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Controller } from "react-hook-form";
import { useDispatch } from "react-redux";
import AddProductVariant from "./AddProductVariant";
import TextFeild from "../../components/CommonComponents/TextFeild";
import addImage from "../../assets/images/add-image-icon.svg";
import uploadImageIcon from "../../assets/images/upload-image-icon.svg";
import addEditProductStyles from "./addEditProduct.styles";
import AddNewVariantTypeModal from "./AddNewVariantTypeModal";
import VariantDeleteModal from "./VariantDeleteModal";
import { fetchOptionTypes } from "../../store/reducers/createProfile/apithunk";
import ImageCropper from "../../components/CommonComponents/ImageCroper";
import { uploadImage } from "../../store/reducers/authentication/apiThunk";
import { ToastNotifyError, ToastNotifySuccess } from "../../components/CommonComponents/Toast/ToastNotify";
import rollbar from "../../rollbarConfig";

const VariantSection = ({
  sxContainer,
  control,
  errors,
  setValue,
  watch,
  selectedVariants,
  completedVariants,
  variantCombinations,
  variantOptionsMap,
  availableVariantTypes,
  productMedia,
  variantSelectedImages,
  openVariantImageModal,
  currentVariantIndex,
  isEditMode,
  setSelectedVariants,
  setVariantOptionsMap,
  setAvailableVariantTypes,
  handleRemoveVariant,
  handleVariantDone,
  handleRemoveCompletedVariant,
  handleEditCompletedVariant,
  handleDiscardCompletedVariant,
  editingVariantIds,
  handleOpenVariantImageModal,
  handleCloseVariantImageModal,
  handleToggleVariantImageSelection,
  handleSaveVariantImages,
  setProductMedia,
  setVariantSelectedImages,
  handleDeleteVariantCombination,
  handleVariantChange,
  handleSaveVariantTypes,
}) => {
  const theme = useTheme();
  const styles = addEditProductStyles(theme);
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [openAddNewTypeModal, setOpenAddNewTypeModal] = useState(false);
  const [openCropperModal, setOpenCropperModal] = useState(false);
  const [currentImageUrl, setCurrentImageUrl] = useState(null);
  const [isUploadingMedia, setIsUploadingMedia] = useState(false);
  const [openDeleteVariantModal, setOpenDeleteVariantModal] = useState(false);
  const [variantToDelete, setVariantToDelete] = useState(null);

  const hasShopifyVariants = variantCombinations?.some(variant => variant?.variantData?.shopify_variant_id);
  return (
    <Box sx={{ ...styles.addVariantSection, ...sxContainer }}>
      <Typography sx={styles.subHeading}>
        Add Variants
      </Typography>

      {/* Variant Type section with tooltip */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: completedVariants?.length === 0 ? 'space-between' : 'flex-start', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Typography sx={styles.commonTitle3}>
              Variant Type
            </Typography>
            <Tooltip
              title="You can add multiple variants for a product."
              placement={isSmallScreen ? "top" : "right"}
              arrow
            >
              <InfoIcon sx={{ color: '#656565', fontSize: '18px', cursor: 'pointer' }} />
            </Tooltip>
          </Box>

          {completedVariants?.length === 0 && completedVariants?.length < 3 && selectedVariants?.length === 0 && !hasShopifyVariants && (
            <Button
              variant='outlined'
              color='primary'
              onClick={(e) => setAnchorEl(e.currentTarget)}
              endIcon={open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              sx={styles.addVariantButton}
            >
              + Add Variant Type
            </Button>
          )}
        </Box>

        {/* Menu for Add Variant Type dropdown */}
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={() => setAnchorEl(null)}
          PaperProps={{
            sx: {
              mt: 1,
              minWidth: 200,
              maxHeight: 300,
              display: 'flex',
              flexDirection: 'column',
            }
          }}
        >
          <Box sx={{
            maxHeight: '200px',
            overflowY: 'auto',
            flex: 1,
          }}>
            {availableVariantTypes?.length > 0 ? (
              <>
                <MenuItem disabled sx={{
                  fontFamily: 'Lexend',
                  fontSize: '14px',
                  fontWeight: 400,
                  color: "#656565",
                  py: 1.5,
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <InfoIcon sx={{ fontSize: '16px' }} />
                    You can select upto 3 variant types
                  </Box>
                </MenuItem>
                {availableVariantTypes?.map((optionType) => (
                  <MenuItem
                    key={optionType?.id}
                    onClick={() => {
                      const newVariant = {
                        id: Date.now(),
                        type: optionType,
                      };
                      setSelectedVariants((prev) => [...prev, newVariant]);
                      setAvailableVariantTypes((prev) => prev?.filter((type) => type?.id !== optionType?.id));
                      setAnchorEl(null);
                    }}
                    sx={{
                      fontFamily: 'Lexend',
                      fontSize: '14px',
                      py: 1.5,
                    }}
                  >
                    {optionType?.name}
                  </MenuItem>
                ))}
              </>
            ) : (
              <MenuItem disabled sx={{ fontFamily: 'Lexend', fontSize: '14px' }}>
                No variant types available
              </MenuItem>
            )}
          </Box>
          <Divider />
          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              setAnchorEl(null);
              setOpenAddNewTypeModal(true);
            }}
            sx={{
              fontFamily: 'Lexend',
              fontSize: '14px',
              py: 1.5,
              color: theme.palette?.primary?.main || '#0F0F0F',
              fontWeight: 400,
              position: 'sticky',
              bottom: 0,
              backgroundColor: theme.palette?.customColor?.white || '#FFFFFF',
              '&:hover': {
                backgroundColor: theme.palette?.action?.hover || '#F5F5F5',
              }
            }}
          >
            +Create Custom Option
          </MenuItem>
        </Menu>

        {/* Show completed variant types with chips - box with edit/delete icons outside */}
        {console.log("VariantSection - completedVariants:", completedVariants, "length:", completedVariants?.length)}
        {completedVariants?.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
            {completedVariants?.map((completedVariant) => (
              <Box key={completedVariant?.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {/* Box with variant type name and chips */}
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  p: 1.5,
                  border: `1px solid ${theme.palette?.customColor?.secondaryThird || "#E0E0E0"}`,
                  flex: 1,
                  minWidth: 0,
                  position: 'relative'
                }}>
                  <Typography sx={{ fontFamily: 'Lexend', fontSize: '14px', fontWeight: 400, whiteSpace: 'nowrap' }}>
                    {completedVariant?.type?.name || 'Variant Type'}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                    {completedVariant?.selectedOptions?.map((option, idx) => (
                      <Chip
                        key={option?.id || idx}
                        label={option?.name || option}
                        size="small"
                        sx={{
                          backgroundColor: theme.palette?.action?.hover || '#F5F5F5',
                          color: theme.palette?.primary?.main || '#0F0F0F',
                          fontFamily: 'Lexend',
                          fontSize: '14px',
                          fontWeight: 400,
                          borderRadius: 'unset !important',
                        }}
                      />
                    ))}
                    {editingVariantIds?.has(completedVariant?.id) && handleDiscardCompletedVariant && (
                      <IconButton
                        size="small"
                        onClick={() => handleDiscardCompletedVariant(completedVariant?.id)}
                        sx={{
                          color: theme.palette?.customColor?.red || "#D32F2F",
                          padding: '4px',
                          '&:hover': {
                            backgroundColor: theme.palette?.action?.hover || "rgba(0, 0, 0, 0.04)",
                          }
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                </Box>
                {!hasShopifyVariants && (
                  <IconButton
                    size="small"
                    onClick={() => handleEditCompletedVariant(completedVariant)}
                    sx={{
                      color: theme.palette?.primary?.main || "#0F0F0F",
                      '&:hover': {
                        backgroundColor: theme.palette?.action?.hover || "rgba(0, 0, 0, 0.04)",
                      },
                    }}
                  >
                    <EditOutlinedIcon fontSize="small" />
                  </IconButton>
                )}
                {!hasShopifyVariants && (
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveCompletedVariant(completedVariant?.id)}
                    sx={{
                      color: theme.palette?.customColor?.red || "#D32F2F",
                      '&:hover': {
                        backgroundColor: theme.palette?.action?.hover || "rgba(0, 0, 0, 0.04)",
                      },
                    }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            ))}
          </Box>
        )}

        {/* Add Variant button with dropdown - positioned below variant boxes when variants exist */}
        {completedVariants?.length > 0 && completedVariants?.length < 3 && selectedVariants?.length === 0 && !hasShopifyVariants && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2, mt: 1 }}>
            <Button
              variant='outlined'
              color='primary'
              onClick={(e) => setAnchorEl(e.currentTarget)}
              endIcon={open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              sx={styles.addVariantButton}
            >
              + Add Variant Type
            </Button>
          </Box>
        )}
      </Box>

      {/* Configuration boxes for variants being set up */}
      <Box sx={styles.variantsList}>
        {selectedVariants?.map((variant, index) => (
          <AddProductVariant
            key={index}
            variantId={variant?.id}
            variantType={variant?.type}
            variantOptions={variantOptionsMap[variant?.type?.id] || []}
            initialSelectedOptions={variant?.selectedOptions || []}
            control={control}
            errors={errors}
            isEditing={editingVariantIds?.has(variant?.originalCompletedVariantId)}
            onDiscard={variant?.originalCompletedVariantId 
              ? () => handleDiscardCompletedVariant(variant?.originalCompletedVariantId)
              : () => handleRemoveVariant(variant?.id)
            }
            onValueChange={(field, value) => {
              setValue(field, value);

              // If variant options are being set, store them in the map
              if (field?.includes('variantOptions') && variant?.type) {
                setVariantOptionsMap((prev) => ({
                  ...prev,
                  [variant?.type?.id]: value,
                }));
              }
            }}
            onRemoveVariant={(variantId) => {
              handleRemoveVariant(variantId);
            }}
            onVariantDone={(variantId, variantType, selectedOptions) => {
              handleVariantDone(variantId, variantType, selectedOptions);
            }}
            onVariantTypeSelected={(variantType) => {
              // Remove variant type from available list when selected
              setAvailableVariantTypes((prev) =>
                prev?.filter((type) => type?.id !== variantType?.id)
              );

              // Update the variant object with the selected type
              setSelectedVariants((prev) =>
                prev?.map((v) =>
                  v.id === variant.id ? { ...v, type: variantType } : v
                )
              );

              // Store variant options in map when variant type is selected
              // This will be updated when options are fetched in AddProductVariant
            }}
            onVariantTypeAdded={(variantType) => {
              // Add variant type back to available list
              if (!availableVariantTypes?.find((t) => t?.id === variantType?.id)) {
                setAvailableVariantTypes((prev) => [...prev, variantType]);
              }
            }}
            availableVariantTypes={availableVariantTypes}
            isEditMode={isEditMode}
          />
        ))}
      </Box>
      <Divider sx={{ backgroundColor: '#CBCBCB' }} />

      {completedVariants?.length > 0 && isEditMode && handleSaveVariantTypes && !hasShopifyVariants && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, mb: 2 }}>
          <Button
            variant='contained'
            onClick={handleSaveVariantTypes}
            disabled={selectedVariants?.length > 0 || editingVariantIds?.size > 0}
            sx={{
              backgroundColor: theme.palette?.primary?.main || "#0F0F0F",
              color: theme.palette?.customColor?.white || "#FFFFFF",
              fontFamily: 'Lexend',
              fontSize: '14px',
              fontWeight: 400,
              textTransform: 'none',
              borderRadius: '4px',
              px: 3,
              py: 1,
              '&:hover': {
                backgroundColor: theme.palette?.primary?.dark || "#000000",
              },
              '&.Mui-disabled': {
                backgroundColor: theme.palette?.action?.disabledBackground || "rgba(0, 0, 0, 0.12)",
                color: theme.palette?.action?.disabled || "rgba(0, 0, 0, 0.26)",
              }
            }}
          >
            Save & Update Variant
          </Button>
        </Box>
      )}

      {/* Variants list - show all combinations */}
      {variantCombinations?.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography sx={{ fontFamily: 'Lexend', fontSize: '16px', fontWeight: 500, mb: "12px" }}>
            Variants
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: "12px" }}>
            {variantCombinations?.map((combination, index) => {
              // Construct display name from option values only (e.g., "XL" or "XL/Red")
              let displayName = '';

              // If combination is an array of option objects (from generateCombinations)
              if (Array.isArray(combination) && combination?.length > 0) {
                // Extract just the option value names, not type names
                displayName = combination?.map(opt => {
                  // Get the value name (could be 'name', 'value', or 'display_name')
                  return opt?.name || opt?.value || opt?.display_name || opt;
                }).join('/');
              }
              // If combination has options as an object (from API: {Size: "Xl", Color: "Red"})
              else if (combination?.options && typeof combination?.options === 'object' && !Array.isArray(combination?.options)) {
                // Extract values from the options object
                // options: {Size: "Xl", Color: "Red"} -> ["Xl", "Red"] -> "Xl/Red"
                const optionValues = Object.values(combination?.options)?.filter(Boolean);
                displayName = optionValues?.join('/');
              }
              // If combination has option_values_with_hex (from API)
              else if (combination?.option_values_with_hex && typeof combination?.option_values_with_hex === 'object') {
                // Extract values from option_values_with_hex object
                // option_values_with_hex: {Size: {value: "Xl", hex_code: null}} -> ["Xl"] -> "Xl"
                const optionValues = Object.values(combination?.option_values_with_hex)
                  ?.map(item => item?.value || item)
                  ?.filter(Boolean);
                displayName = optionValues?.join('/');
              }
              // If combination has options array (from API - old format)
              else if (combination?.options && Array.isArray(combination?.options)) {
                // Try to get option value names from completedVariants or variantOptionsMap
                const optionValueNames = combination?.options?.map(opt => {
                  // First try to get from the option object itself
                  if (opt?.name || opt?.value || opt?.display_name) {
                    return opt?.name || opt?.value || opt?.display_name;
                  }

                  // If we have option ID and typeId, try to find in completedVariants
                  if (opt?.id && opt?.typeId && completedVariants?.length > 0) {
                    const completedVariant = completedVariants?.find(cv => cv?.type?.id === opt?.typeId);
                    if (completedVariant) {
                      const optionValue = completedVariant?.selectedOptions?.find(so => so?.id === opt?.id);
                      if (optionValue) {
                        return optionValue?.name || optionValue?.value || optionValue?.display_name;
                      }
                    }
                  }

                  // Fallback
                  return `Option ${opt?.id}`;
                })?.filter(Boolean);

                displayName = optionValueNames?.join('/');
              }
              // If combination has variant_title (from API)
              else if (combination.variant_title) {
                displayName = combination?.variant_title;
              }
              // If combination has displayName, try to extract just the values
              else if (combination.displayName || combination.display_name) {
                const fullName = combination?.displayName || combination?.display_name;
                // Remove type names like "Size: " or "Color: " and keep only values
                // Pattern: "Size: XL" -> "XL" or "Size: XL, Color: Red" -> "XL/Red"
                // Also handle patterns like "Size: XL / Color: Red"
                displayName = fullName
                  .replace(/\w+:\s*/g, '') // Remove "Type: " patterns
                  .replace(/,\s*/g, '/') // Replace commas with slashes
                  .replace(/\s*\/\s*/g, '/') // Normalize slashes
                  .trim();
              }

              // Fallback to variant ID or index
              if (!displayName) {
                displayName = combination.id ? `Variant ${combination.id}` : `Variant ${index + 1}`;
              }

              // Get media from API response (variant_img) or form value
              const variantMedia = combination.media || combination.variant_img || [];

              return (
                <Box
                  key={combination.id || index}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: { xs: '10px', sm: '20px', md: '28px', lg: '32px' },
                    }}
                  >
                    {/* Image preview with selection count */}
                    <Box
                      onClick={() => handleOpenVariantImageModal(index)}
                      sx={{
                        position: 'relative',
                        width: '68px',
                        height: '68px',
                        border: `1px dashed ${errors?.variantCombinations?.[index]?.media
                          ? theme.palette?.error?.main || '#D32F2F'
                          : theme.palette?.customColor?.secondaryThird || "#E0E0E0"}`,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        flexShrink: 0,
                        cursor: 'pointer',
                        overflow: 'hidden',
                      }}
                    >
                      {(() => {
                        const formMediaValue = watch(`variantCombinations.${index}.media`);
                        const apiMedia = variantMedia || [];
                        let selectedMedia = formMediaValue && formMediaValue?.length > 0 ? formMediaValue : apiMedia;

                        const selectedIndices = variantSelectedImages?.[index] || [];
                        const selectedImageUrlsFromModal = selectedIndices?.map(idx => productMedia?.[idx])?.filter(Boolean);

                        const finalMedia = (formMediaValue && formMediaValue?.length > 0) 
                          ? formMediaValue 
                          : (selectedImageUrlsFromModal?.length > 0 ? selectedImageUrlsFromModal : selectedMedia);
                        const firstImage = finalMedia && finalMedia?.length > 0 ? finalMedia[0] : null;
                        const imageCount = finalMedia ? finalMedia?.length : 0;

                        return (
                          <>
                            {firstImage ? (
                              <img
                                src={firstImage}
                                alt={`Variant ${index + 1}`}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                }}
                              />
                            ) : (
                              <img src={addImage} alt='addImage' width='20px' />
                            )}
                            {imageCount > 0 && (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  top: 4,
                                  right: 4,
                                  width: '20px',
                                  height: '20px',
                                  borderRadius: '50%',
                                  backgroundColor: theme.palette?.primary?.main || '#0F0F0F',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: '#FFFFFF',
                                  fontSize: '10px',
                                  fontWeight: 500,
                                  fontFamily: 'Lexend',
                                }}
                              >
                                {imageCount}
                              </Box>
                            )}
                          </>
                        );
                      })()}
                    </Box>

                    {/* Variant Name - combination with "/" */}
                    <Typography sx={{
                      fontFamily: 'Lexend',
                      fontSize: '14px',
                      fontWeight: 400,
                      color: theme.palette?.primary?.main || '#0F0F0F',
                      flex: 1,
                      minWidth: 0,
                    }}>
                      {displayName}
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                      <Box sx={{ width: '118px' }}>
                        <Controller
                          name={`variantCombinations.${index}.price`}
                          control={control}
                          defaultValue=''
                          render={({ field }) => (
                            <TextFeild
                              {...field}
                              id={`variantCombinations.${index}.price`}
                              label='Price (in USD)'
                              placeholder='$00.00'
                              variant='outlined'
                              fullWidth
                              type='number'
                              inputProps={{
                                min: 0,
                                step: "0.01",
                              }}
                              onChange={(e) => {
                                field.onChange(e);
                                if (handleVariantChange) {
                                  handleVariantChange(index, 'price', e.target.value);
                                }
                              }}
                            />
                          )}
                        />
                      </Box>

                      <Box sx={{ width: '118px' }}>
                        <Controller
                          name={`variantCombinations.${index}.quantity`}
                          control={control}
                          defaultValue='0'
                          render={({ field }) => (
                            <TextFeild
                              {...field}
                              id={`variantCombinations.${index}.quantity`}
                              label='Quantity'
                              placeholder='0'
                              variant='outlined'
                              fullWidth
                              type='number'
                              inputProps={{
                                min: 0,
                                step: 1,
                              }}
                              onChange={(e) => {
                                const newValue = e.target.value;
                                const currentValue = field.value?.toString() || '';
                                let finalValue = newValue;
                                if (currentValue === '0' && newValue.length > 1 && newValue.startsWith('0')) {
                                  finalValue = newValue.replace(/^0+/, '') || '0';
                                  field.onChange({ ...e, target: { ...e.target, value: finalValue } });
                                } else {
                                  field.onChange(e);
                                  finalValue = newValue;
                                }

                                if (handleVariantChange) {
                                  handleVariantChange(index, 'quantity', finalValue);
                                }
                              }}
                            />
                          )}
                        />
                      </Box>
                    </Box>
                    {!combination?.variantData?.shopify_variant_id && (
                      <Tooltip title="Delete variant" arrow>
                        <IconButton
                          onClick={() => {
                            setVariantToDelete({ index, name: displayName });
                            setOpenDeleteVariantModal(true);
                          }}
                          sx={{
                            color: '#B60218',
                            padding: '8px',
                            '&:hover': {
                              backgroundColor: 'rgba(182, 2, 24, 0.08)',
                            },
                          }}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: '24px' }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>

                  {errors?.variantCombinations?.[index]?.media && (
                    <Typography
                      sx={{
                        fontFamily: 'Lexend',
                        fontSize: '12px',
                        fontWeight: 400,
                        color: theme.palette?.error?.main || '#D32F2F',
                        lineHeight: 1.2,
                      }}
                    >
                      {errors.variantCombinations[index].media.message}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      <Modal
        open={openVariantImageModal}
        onClose={(event, reason) => {
          if (reason === 'backdropClick') {
            return;
          }
          handleCloseVariantImageModal();
        }}
        closeAfterTransition
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backdropFilter: "blur(3px)",
        }}
      >
        <Box
          sx={{
            position: 'relative',
            backgroundColor: 'white',
            borderRadius: '8px',
            p: 3,
            maxWidth: '600px',
            width: '90vw',
            maxHeight: '90vh',
            overflow: 'auto',
          }}
        >
          <Typography sx={{
            fontFamily: 'Lexend',
            fontSize: '20px',
            fontWeight: 500,
            mb: 1,
            color: theme.palette?.primary?.main || '#0F0F0F',
          }}>
            Select Variant Image
          </Typography>

          <Typography sx={{
            fontFamily: 'Lexend',
            fontSize: '14px',
            fontWeight: 400,
            mb: 3,
            color: theme.palette?.text?.secondary || '#656565',
          }}>
            You can select upto 1 image only
          </Typography>

          <Box
            component="label"
            htmlFor="variant-image-upload"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              border: `2px dashed ${theme.palette?.customColor?.secondaryThird || "#E0E0E0"}`,
              borderRadius: '4px',
              p: 4,
              mb: 3,
              cursor: 'pointer',
              backgroundColor: theme.palette?.action?.hover || '#FAFAFA',
              '&:hover': {
                borderColor: theme.palette?.primary?.main || '#0F0F0F',
                backgroundColor: theme.palette?.action?.hover || '#F5F5F5',
              }
            }}
          >
            <input
              type="file"
              id="variant-image-upload"
              accept="image/png,image/jpeg,image/jpg"
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  const maxSize = 5 * 1024 * 1024; // 5MB
                  if (file.size > maxSize) {
                    ToastNotifyError("File size must be less than 5MB");
                    e.target.value = "";
                    return;
                  }
                  setCurrentImageUrl(URL.createObjectURL(file));
                  setOpenCropperModal(true);
                  e.target.value = "";
                }
              }}
            />
            <img src={uploadImageIcon} alt='upload' width='24px' style={{ marginBottom: '8px' }} />
            <Typography sx={{
              fontFamily: 'Lexend',
              fontSize: '14px',
              fontWeight: 400,
              color: theme.palette?.primary?.main || '#0F0F0F',
              mb: 0.5,
            }}>
              Upload New Media
            </Typography>
            <Typography sx={{
              fontFamily: 'Lexend',
              fontSize: '12px',
              fontWeight: 400,
              color: theme.palette?.text?.secondary || '#656565',
            }}>
              Supported format .png, .jpg
            </Typography>
          </Box>

          {productMedia?.length > 0 && (
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 2,
              mb: 3,
            }}>
              {productMedia?.map((mediaUrl, imageIndex) => {
                const selectedIndices = variantSelectedImages[currentVariantIndex] || [];
                const isSelected = selectedIndices?.length > 0 && selectedIndices[0] === imageIndex;

                return (
                  <Box
                    key={imageIndex}
                    onClick={() => {
                      handleToggleVariantImageSelection(imageIndex);
                    }}
                    sx={{
                      position: 'relative',
                      width: '100%',
                      paddingTop: '100%',
                      border: `2px solid ${isSelected ? "#CBCBCB" : "#E0E0E0"}`,
                      overflow: 'hidden',
                      cursor: 'pointer',
                      '&:hover': {
                        borderColor: theme.palette?.primary?.main || '#0F0F0F',
                      }
                    }}
                  >
                    <img
                      src={mediaUrl}
                      alt={`Media ${imageIndex + 1}`}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        border: `2px solid ${isSelected ? '#0F0F0F' : '#CBCBCB'}`,
                        backgroundColor: isSelected ? 'transparent' : 'rgba(255, 255, 255)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {isSelected && (
                        <Box
                          sx={{
                            width: '12px',
                            height: '12px',
                            borderRadius: '50%',
                            backgroundColor: '#0F0F0F',
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}

          <Box sx={{
            display: 'flex',
            gap: 2,
            justifyContent: 'flex-end',
          }}>
            <Button
              variant="outlined"
              onClick={handleCloseVariantImageModal}
              sx={{
                borderColor: theme.palette?.customColor?.secondaryThird || "#E0E0E0",
                color: theme.palette?.primary?.main || '#0F0F0F',
                fontFamily: 'Lexend',
                fontSize: '14px',
                fontWeight: 400,
                textTransform: 'none',
                borderRadius: '4px',
                px: 3,
                py: 1,
                '&:hover': {
                  borderColor: theme.palette?.primary?.main || '#0F0F0F',
                }
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveVariantImages}
              sx={{
                backgroundColor: theme.palette?.primary?.main || '#0F0F0F',
                color: '#FFFFFF',
                fontFamily: 'Lexend',
                fontSize: '14px',
                fontWeight: 400,
                textTransform: 'none',
                borderRadius: '4px',
                px: 3,
                py: 1,
                '&:hover': {
                  backgroundColor: theme.palette?.primary?.dark || '#000000',
                }
              }}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Image Cropper Modal */}
      <Modal
        open={openCropperModal}
        onClose={() => {
          setOpenCropperModal(false);
          setCurrentImageUrl(null);
        }}
        closeAfterTransition
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backdropFilter: "blur(3px)",
        }}
      >
        <Box sx={{
          position: 'relative',
          backgroundColor: 'white',
          borderRadius: '8px',
          p: 3,
          maxWidth: '90vw',
          maxHeight: '90vh',
          overflow: 'auto',
        }}>
          {currentImageUrl && (
            <ImageCropper
              src={currentImageUrl}
              aspectRatio={1}
              onCancel={() => {
                setOpenCropperModal(false);
                setCurrentImageUrl(null);
              }}
              onCropComplete={async ({ file }) => {
                setIsUploadingMedia(true);
                try {
                  const response = await dispatch(uploadImage(file));
                  const { key, uploadResponse } = response?.payload || {};
                  const { status } = uploadResponse || {};

                  if (status === 200) {
                    const finalImageUrl = process.env.REACT_APP_S3_IMAGES_URL + key;

                    // Add to productMedia if not already present
                    if (!productMedia.includes(finalImageUrl)) {
                      const updatedMedia = [...productMedia, finalImageUrl];
                      // Update parent's productMedia state
                      if (setProductMedia) {
                        setProductMedia(updatedMedia);
                        setValue('productMedia', updatedMedia);
                      }
                      // Set the newly uploaded image as selected (use the new index from updated array)
                      // Only update selection state for visual feedback, don't save to variant yet
                      // The image will be saved when user clicks "Save" button
                      const newIndex = updatedMedia?.length - 1;
                      // Directly update selection state since we know the new index
                      setVariantSelectedImages((prev) => ({
                        ...prev,
                        [currentVariantIndex]: [newIndex]
                      }));
                    } else {
                      // Image already exists, just select it (only visual feedback)
                      const existingIndex = productMedia?.indexOf(finalImageUrl);
                      handleToggleVariantImageSelection(existingIndex);
                    }

                    ToastNotifySuccess("Image uploaded successfully!");
                    setOpenCropperModal(false);
                    setCurrentImageUrl(null);
                  } else {
                    ToastNotifyError("Failed to upload image");
                  }
                } catch (err) {
                  rollbar.error("Error uploading variant image", err);
                  ToastNotifyError("Failed to upload image");
                } finally {
                  setIsUploadingMedia(false);
                }
              }}
            />
          )}
          {isUploadingMedia && (
            <Box sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              borderRadius: '8px',
            }}>
              <CircularProgress />
            </Box>
          )}
        </Box>
      </Modal>

      {/* Add New Variant Type Modal */}
      <AddNewVariantTypeModal
        open={openAddNewTypeModal}
        onClose={() => setOpenAddNewTypeModal(false)}
        onSuccess={async (newOptionType) => {
          // Refresh option types list
          const refreshResponse = await dispatch(fetchOptionTypes());
          const refreshData = refreshResponse?.payload?.data || [];

          // Update available variant types in parent
          if (refreshData?.length > 0) {
            setAvailableVariantTypes(refreshData);
          }
        }}
      />

      {/* Variant Delete Confirmation Modal */}
      <VariantDeleteModal
        open={openDeleteVariantModal}
        onClose={() => {
          setOpenDeleteVariantModal(false);
          setVariantToDelete(null);
        }}
        onConfirm={(variantIndex) => {
          if (variantIndex !== null && variantIndex !== undefined) {
            handleDeleteVariantCombination(variantIndex);
          }
        }}
        variantIndex={variantToDelete?.index}
        variantName={variantToDelete?.name}
      />
    </Box>
  );
};

export default VariantSection;

